import { useState, useCallback, useRef, useMemo, useEffect } from 'react';

import { useActions } from '../../../shared/common/hooks/useActions';
import { useTimerCleanup } from '../../../shared/common/hooks/useTimerCleanup';
import {
  DashboardComponent,
  GridItemLayout,
  GRID_CONSTANTS,
} from '../types';
import { migrateLayoutData } from '../utils/layoutMigration';

/**
 * Возвращаемые значения хука.
 */
interface UseDashboardLayoutReturn {
  /** Компоненты дашборда */
  dashboardComponents: DashboardComponent[];
  /** Установить компоненты */
  setDashboardComponents: React.Dispatch<React.SetStateAction<DashboardComponent[]>>;
  /** Масштабированный layout для grid */
  layout: GridItemLayout[];
  /** Количество колонок сетки */
  gridCols: number;
  /** Ширина сетки в пикселях */
  gridWidth: number;
  /** Высота контейнера в пикселях */
  calculatedHeight: number;
  /** Обработчик изменения layout */
  onLayoutChange: (newLayout: GridItemLayout[]) => void;
  /** Флаг режима resize */
  isResizing: boolean;
  /** Флаг режима drag */
  isDragging: boolean;
  /** Флаг разрешения resize (отключает preventCollision) */
  allowResize: boolean;
  /** Обработчик начала drag */
  onDragStart: (layout: GridItemLayout[], oldItem: GridItemLayout, newItem: GridItemLayout) => void;
  /** Обработчик окончания drag */
  onDragStop: (layout: GridItemLayout[], oldItem: GridItemLayout, newItem: GridItemLayout) => void;
  /** Пропустить следующий onLayoutChange (для группового перемещения) */
  skipNextLayoutChange: () => void;
  /** Обработчик начала resize */
  onResizeStart: (currentLayout: GridItemLayout[], oldItem: GridItemLayout) => void;
  /** Обработчик resize */
  onResize: (
    currentLayout: GridItemLayout[],
    oldItem: GridItemLayout,
    newItem: GridItemLayout,
    placeholder: GridItemLayout
  ) => void;
  /** Обработчик окончания resize */
  onResizeStop: (
    currentLayout: GridItemLayout[],
    oldItem: GridItemLayout,
    newItem: GridItemLayout
  ) => void;
}

/**
 * Проверка, изменился ли layout элемент.
 */
const hasLayoutItemChanged = (
  current: GridItemLayout,
  incoming: GridItemLayout
): boolean => {
  return (
    current.x !== incoming.x ||
    current.y !== incoming.y ||
    current.w !== incoming.w ||
    current.h !== incoming.h
  );
};

/**
 * Хук для управления layout дашборда.
 * Обрабатывает:
 * - Drag & drop блоков
 * - Resize блоков
 * - Миграцию старых данных
 * - Вычисление размеров сетки
 *
 * Оптимизации:
 * - onLayoutChange проверяет, изменился ли layout, перед обновлением state
 * - isResizing проверяется через ref
 * - Redux sync через ref (useActions не мемоизирует)
 * - Cleanup body overflow при размонтировании
 */
export const useDashboardLayout = (
  initialComponents: DashboardComponent[] = []
): UseDashboardLayoutReturn => {
  const { setDashboards } = useActions();
  const { safeSetTimeout } = useTimerCleanup();

  const [dashboardComponents, setDashboardComponents] = useState<DashboardComponent[]>(initialComponents);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [allowResize, setAllowResize] = useState(false);

  // Refs для отслеживания состояния (избегают зависимостей в useCallback)
  const isResizingRef = useRef(false);
  const layoutBeforeResizeRef = useRef<GridItemLayout[] | null>(null);
  const resizingItemRef = useRef<string | null>(null);
  const skipNextLayoutChangeRef = useRef(false);

  // Ref для Redux action (useActions() не мемоизирует — создаёт новые ссылки каждый рендер)
  const setDashboardsRef = useRef(setDashboards);
  setDashboardsRef.current = setDashboards;

  // Синхронизируем ref с state
  isResizingRef.current = isResizing;

  /**
   * Cleanup: восстановить body overflow при размонтировании.
   */
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  /**
   * Масштабированный layout для grid.
   */
  const layout = useMemo(() => {
    return dashboardComponents.map((component) => {
      return migrateLayoutData(component.layoutData, component.componentType, component.title);
    });
  }, [dashboardComponents]);

  /**
   * Количество колонок сетки (автоматическое расширение).
   */
  const gridCols = useMemo(() => {
    if (layout.length === 0) return GRID_CONSTANTS.MIN_COLS;

    const maxRight = layout.reduce((max, item) => {
      const right = (item.x || 0) + (item.w || 1);
      return Math.max(max, right);
    }, 0);

    return Math.max(GRID_CONSTANTS.MIN_COLS, maxRight + 20);
  }, [layout]);

  /**
   * Ширина сетки в пикселях.
   */
  const gridWidth = gridCols * GRID_CONSTANTS.CELL_WIDTH;

  /**
   * Высота контейнера в пикселях.
   */
  const calculatedHeight = useMemo(() => {
    const minHeight = GRID_CONSTANTS.MIN_ROWS * GRID_CONSTANTS.CELL_HEIGHT;

    if (layout.length === 0) return minHeight;

    const maxBottom = layout.reduce((max, item) => {
      const bottom = (item.y || 0) + (item.h || 1);
      return Math.max(max, bottom);
    }, 0);

    const contentHeight =
      maxBottom * GRID_CONSTANTS.CELL_HEIGHT +
      (maxBottom + 1) * GRID_CONSTANTS.MARGIN +
      200;

    return Math.max(minHeight, contentHeight);
  }, [layout]);

  /**
   * Обработчик изменения layout.
   * Оптимизации:
   * - setState callback — не зависит от dashboardComponents
   * - Проверяет, изменился ли layout, перед созданием новых объектов
   * - Возвращает prev если ничего не изменилось (предотвращает лишние ререндеры)
   */
  const onLayoutChange = useCallback(
    (newLayout: GridItemLayout[]) => {
      if (isResizingRef.current || skipNextLayoutChangeRef.current) {
        return;
      }

      setDashboardComponents((prev) => {
        let hasAnyChange = false;

        const updated = prev.map((component) => {
          const layoutItem = newLayout.find((item) => item.i === component.layoutData.i);
          if (!layoutItem) return component;

          // Проверяем, изменились ли координаты/размеры
          if (!hasLayoutItemChanged(component.layoutData, layoutItem) && component.layoutData.scaled) {
            return component; // Тот же объект — без новой ссылки
          }

          hasAnyChange = true;
          return {
            ...component,
            layoutData: { ...layoutItem, scaled: true },
          };
        });

        // Если ничего не изменилось — возвращаем prev (та же ссылка → нет ререндера)
        return hasAnyChange ? updated : prev;
      });
    },
    [] // Стабильная ссылка
  );

  /**
   * Обработчик начала drag.
   */
  const onDragStart = useCallback(
    (_layout: GridItemLayout[], _oldItem: GridItemLayout, _newItem: GridItemLayout) => {
      setIsDragging(true);
    },
    []
  );

  /**
   * Обработчик окончания drag.
   */
  const onDragStop = useCallback(
    (_layout: GridItemLayout[], _oldItem: GridItemLayout, _newItem: GridItemLayout) => {
      setIsDragging(false);
    },
    []
  );

  /**
   * Пропустить следующий onLayoutChange.
   * Используется для группового перемещения блоков.
   */
  const skipNextLayoutChange = useCallback(() => {
    skipNextLayoutChangeRef.current = true;
    safeSetTimeout(() => {
      skipNextLayoutChangeRef.current = false;
    }, 100);
  }, [safeSetTimeout]);

  /**
   * Обработчик начала resize.
   */
  const onResizeStart = useCallback(
    (currentLayout: GridItemLayout[], oldItem: GridItemLayout) => {
      setIsResizing(true);
      setAllowResize(true);
      layoutBeforeResizeRef.current = currentLayout.map((item) => ({ ...item }));
      resizingItemRef.current = oldItem.i;
      document.body.style.overflow = 'hidden';
    },
    []
  );

  /**
   * Обработчик resize (фиксация позиции, сдвиг соседей).
   */
  const onResize = useCallback(
    (
      currentLayout: GridItemLayout[],
      _oldItem: GridItemLayout,
      newItem: GridItemLayout,
      placeholder: GridItemLayout
    ) => {
      if (!layoutBeforeResizeRef.current || !resizingItemRef.current) return;

      const originalItem = layoutBeforeResizeRef.current.find(
        (item) => item.i === resizingItemRef.current
      );

      if (!originalItem) return;

      // Фиксируем позицию блока при resize
      newItem.x = originalItem.x;
      newItem.y = originalItem.y;
      placeholder.x = originalItem.x;
      placeholder.y = originalItem.y;

      // Сдвигаем соседние блоки
      const deltaW = newItem.w - originalItem.w;
      const deltaH = newItem.h - originalItem.h;

      if (deltaW !== 0 || deltaH !== 0) {
        const originalRight = originalItem.x + originalItem.w;
        const originalBottom = originalItem.y + originalItem.h;

        currentLayout.forEach((item) => {
          if (item.i === resizingItemRef.current) return;

          const original = layoutBeforeResizeRef.current?.find((o) => o.i === item.i);
          if (!original) return;

          const overlapsY =
            original.y < originalBottom && original.y + original.h > originalItem.y;
          if (original.x >= originalRight && overlapsY && deltaW !== 0) {
            item.x = original.x + deltaW;
          }

          const itemRight = original.x + original.w;
          const overlapsX = original.x < originalRight && itemRight > originalItem.x;
          const isBelow = original.y >= originalBottom;
          if (isBelow && overlapsX && deltaH !== 0) {
            item.y = original.y + deltaH;
          }
        });
      }
    },
    []
  );

  /**
   * Обработчик окончания resize.
   */
  const onResizeStop = useCallback(
    (
      _currentLayout: GridItemLayout[],
      _oldItem: GridItemLayout,
      newItem: GridItemLayout
    ) => {
      if (!layoutBeforeResizeRef.current || !resizingItemRef.current) {
        setIsResizing(false);
        document.body.style.overflow = 'auto';
        return;
      }

      const originalLayout = layoutBeforeResizeRef.current;
      const resizingId = resizingItemRef.current;
      const originalResizing = originalLayout.find((item) => item.i === resizingId);

      if (!originalResizing) {
        setIsResizing(false);
        layoutBeforeResizeRef.current = null;
        resizingItemRef.current = null;
        document.body.style.overflow = 'auto';
        return;
      }

      const deltaH = newItem.h - originalResizing.h;
      const deltaW = newItem.w - originalResizing.w;
      const originalBottom = originalResizing.y + originalResizing.h;
      const originalRight = originalResizing.x + originalResizing.w;

      skipNextLayoutChangeRef.current = true;

      setDashboardComponents((prev) => {
        return prev.map((comp) => {
          const originalItem = originalLayout.find((o) => o.i === comp.layoutData.i);
          if (!originalItem) return comp;

          if (comp.layoutData.i === resizingId) {
            return {
              ...comp,
              layoutData: {
                ...comp.layoutData,
                x: originalResizing.x,
                y: originalResizing.y,
                w: newItem.w,
                h: newItem.h,
                scaled: true,
              },
            };
          }

          let newX = originalItem.x;
          let newY = originalItem.y;

          const overlapsY =
            originalItem.y < originalBottom &&
            originalItem.y + originalItem.h > originalResizing.y;

          if (originalItem.x >= originalRight && overlapsY && deltaW !== 0) {
            newX = originalItem.x + deltaW;
          } else {
            const itemRight = originalItem.x + originalItem.w;
            const overlapsX = originalItem.x < originalRight && itemRight > originalResizing.x;
            const isBelow = originalItem.y >= originalBottom;

            if (isBelow && overlapsX && deltaH !== 0) {
              newY = originalItem.y + deltaH;
            }
          }

          return {
            ...comp,
            layoutData: {
              ...comp.layoutData,
              x: Math.max(0, newX),
              y: Math.max(0, newY),
              w: originalItem.w,
              h: originalItem.h,
              scaled: true,
            },
          };
        });
      });

      layoutBeforeResizeRef.current = null;
      resizingItemRef.current = null;
      document.body.style.overflow = 'auto';

      safeSetTimeout(() => {
        setIsResizing(false);
        skipNextLayoutChangeRef.current = false;
        setAllowResize(false);
      }, 100);
    },
    []
  );

  /**
   * Синхронизация с Redux при изменении компонентов.
   * Используем ref для setDashboards, т.к. useActions() не мемоизирует —
   * каждый рендер возвращает новые ссылки, что вызывало бесконечный цикл.
   */
  useEffect(() => {
    setDashboardsRef.current(dashboardComponents as any);
  }, [dashboardComponents]);

  return {
    dashboardComponents,
    setDashboardComponents,
    layout,
    gridCols,
    gridWidth,
    calculatedHeight,
    onLayoutChange,
    isResizing,
    isDragging,
    allowResize,
    onDragStart,
    onDragStop,
    skipNextLayoutChange,
    onResizeStart,
    onResize,
    onResizeStop,
  };
};

export default useDashboardLayout;
